import type { Track, TakContact } from '@vtak/shared';
import { usePluginDataStore } from '@/stores/plugin-data-store';
import { useContactsStore } from '@/stores/contacts-store';
import type { SearchProvider, SearchResult } from './search-provider';

/** Zoom applied when flying to a live track or contact. */
const TRACK_ZOOM = 14;

/** Cap on combined track + contact matches. */
const MAX_RESULTS = 8;

/** Searches things already on the map (feed tracks + TAK contacts) by callsign. */
export const trackProvider: SearchProvider = {
  id: 'track',
  search: async (query) => searchLive(query.trim().toLowerCase()),
};

/** Match feed tracks and contacts against a lower-cased needle. */
function searchLive(needle: string): SearchResult[] {
  if (needle.length === 0) return [];
  return [...searchTracks(needle), ...searchContacts(needle)].slice(0, MAX_RESULTS);
}

/** All feed tracks across every loaded layer. */
function allTracks(): Track[] {
  return [...usePluginDataStore.getState().data.values()].flatMap((list) => list as Track[]);
}

/** Feed-track matches by callsign substring. */
function searchTracks(needle: string): SearchResult[] {
  return allTracks()
    .filter((track) => includesCaseless(track.callsign, needle))
    .map(trackToResult);
}

/** Contact matches by callsign substring (only those with coordinates). */
function searchContacts(needle: string): SearchResult[] {
  return useContactsStore
    .getState()
    .contacts.filter((contact) => hasCoords(contact) && includesCaseless(contact.callsign ?? '', needle))
    .map(contactToResult);
}

/** Whether a contact carries usable coordinates. */
function hasCoords(contact: TakContact): boolean {
  return typeof contact.lat === 'number' && typeof contact.lon === 'number';
}

/** Case-insensitive substring test. */
function includesCaseless(value: string, needle: string): boolean {
  return value.toLowerCase().includes(needle);
}

/** Map a feed track to a search result. */
function trackToResult(track: Track): SearchResult {
  return {
    label: track.callsign,
    sublabel: track.layerId,
    lat: track.lat,
    lon: track.lon,
    source: 'Tracks',
    zoom: TRACK_ZOOM,
    uid: track.uid,
  };
}

/** Map a TAK contact to a search result. */
function contactToResult(contact: TakContact): SearchResult {
  return {
    label: contact.callsign ?? contact.uid ?? 'Contact',
    sublabel: 'Contact',
    lat: contact.lat as number,
    lon: contact.lon as number,
    source: 'Contacts',
    zoom: TRACK_ZOOM,
    uid: contact.uid,
  };
}
